/**
 * useAuth Hook — Canonical Authentication Hook
 * Provides authentication state and actions for login/logout operations,
 * plus standalone mutation hooks for password management, profile updates,
 * and session management.
 *
 * Combines Zustand auth store with React Query mutations.
 */

import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  useAuthStore,
  useCurrentUser,
  useIsAuthenticated,
  useAuthLoading,
  useAuthInitialized,
  useCurrentWorkspaceId,
} from '@/store/auth.store';
import * as authApi from '@/api/auth.api';
import { clearTokens, getRefreshToken } from '@/utils/token';
import { sanitizeReturnTo, DEFAULT_AFTER_LOGIN_PATH } from '@/utils/returnTo';
import { useSnackbar } from '@/components/feedback/Snackbar/useSnackbar';
import type { LoginFormData, RegisterFormData } from '@/features/auth/schemas';
import { parseApiError } from '@/features/auth/types';
import { PUBLIC_ROUTES } from '@/routes/routes';
import type {
  ForgotPasswordRequest,
  ResetPasswordRequest,
  ChangePasswordRequest,
  UpdateProfileRequest,
} from '@/types/api.types';

// Re-export AuthError type for consumers
export type { AuthError } from '@/features/auth/types';

/**
 * Query key for current user cache seeding
 */
export const CURRENT_USER_QUERY_KEY = ['auth', 'current-user'];

/**
 * Query key for sessions
 */
export const SESSIONS_QUERY_KEY = ['auth', 'sessions'];

/**
 * useAuth Hook
 * Main hook for authentication operations
 *
 * @example
 * ```tsx
 * const { user, isAuthenticated, login, logout, isLoginLoading } = useAuth();
 *
 * const handleSubmit = async (data) => {
 *   try {
 *     await login(data);
 *     // User will be redirected to dashboard
 *   } catch (error) {
 *     // Error is available via loginError
 *   }
 * };
 * ```
 */
export const useAuth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { success: showSuccess, error: showError, info: showInfo } = useSnackbar();

  // Get state from store
  const user = useCurrentUser();
  const isAuthenticated = useIsAuthenticated();
  const isLoading = useAuthLoading();
  const isInitialized = useAuthInitialized();
  const currentWorkspaceId = useCurrentWorkspaceId();

  // Get actions from store
  const { setAuth, setUser, logout: storeLogout } = useAuthStore();

  const completeMcpGatewayLoginHandoff = useCallback(
    async (gatewayState: string, workspaceId?: number) => {
      const handoff = await authApi.createMcpGatewayLoginHandoff({
        gatewayState,
        workspaceId,
      });
      window.location.href = handoff.callbackUrl;
    },
    []
  );

  /**
   * Login mutation
   */
  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginFormData) => {
      const response = await authApi.login({
        email: credentials.email,
        password: credentials.password,
      });
      // Return both API response and rememberMe preference
      return { ...response, rememberMe: credentials.rememberMe };
    },
    onSuccess: async (data) => {
      // Update auth store with user, tokens, and rememberMe preference
      setAuth(
        data.user,
        {
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
        },
        data.rememberMe
      );

      // Seed the query cache with user data
      queryClient.setQueryData(CURRENT_USER_QUERY_KEY, data.user);

      if (data.user.mustChangePassword) {
        navigate(PUBLIC_ROUTES.CHANGE_PASSWORD, { replace: true });
        return;
      }

      const gatewayState = searchParams.get('gateway_state');
      if (searchParams.has('gateway_state')) {
        await completeMcpGatewayLoginHandoff(
          gatewayState ?? '',
          currentWorkspaceId ?? data.user.defaultWorkspaceId ?? undefined
        );
        return;
      }

      // Show success notification
      showSuccess(`Welcome back, ${data.user.firstName}!`);

      // Redirect precedence (per auth-fix-4 spec):
      //   1. valid ?returnTo= query param (survives hard reloads, which lose state)
      //   2. React Router location.state.from (guest-route redirect path)
      //   3. DEFAULT_AFTER_LOGIN_PATH (/dashboard)
      const sanitizedReturnTo = sanitizeReturnTo(searchParams.get('returnTo'));
      const fromLocation = (
        location.state as { from?: { pathname: string; search?: string; hash?: string } }
      )?.from;
      const fromStateUrl = fromLocation
        ? `${fromLocation.pathname}${fromLocation.search || ''}${fromLocation.hash || ''}`
        : null;
      const destination = sanitizedReturnTo ?? fromStateUrl ?? DEFAULT_AFTER_LOGIN_PATH;
      navigate(destination, { replace: true });

      // Fetch full user profile in background (auth response only has partial user)
      try {
        const fullUser = await authApi.getCurrentUser();
        setUser(fullUser);
        queryClient.setQueryData(CURRENT_USER_QUERY_KEY, fullUser);
      } catch {
        // Graceful degradation: partial user is acceptable short-term
        console.warn('Failed to fetch full user profile after login');
      }
    },
    onError: () => {
      showError('Login failed', {
        description: 'Please check your email and password',
      });
    },
  });

  /**
   * Register mutation
   */
  const registerMutation = useMutation({
    mutationFn: async (data: RegisterFormData) => {
      const response = await authApi.register({
        email: data.email,
        password: data.password,
        firstName: data.firstName,
        lastName: data.lastName,
      });
      return response;
    },
    onSuccess: async (data) => {
      setAuth(
        data.user,
        {
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
        },
        false
      );

      // Seed the query cache with user data
      queryClient.setQueryData(CURRENT_USER_QUERY_KEY, data.user);

      // Show success notification
      showSuccess(`Welcome, ${data.user.firstName}!`, {
        description: 'Your account has been created successfully',
      });

      const sanitizedReturnTo = sanitizeReturnTo(searchParams.get('returnTo'));
      navigate(sanitizedReturnTo ?? DEFAULT_AFTER_LOGIN_PATH, { replace: true });

      // Fetch full user profile in background (auth response only has partial user)
      try {
        const fullUser = await authApi.getCurrentUser();
        setUser(fullUser);
        queryClient.setQueryData(CURRENT_USER_QUERY_KEY, fullUser);
      } catch {
        // Graceful degradation: partial user is acceptable short-term
        console.warn('Failed to fetch full user profile after register');
      }
    },
    onError: () => {
      showError('Registration failed', {
        description: 'Please check your information and try again',
      });
    },
  });

  /**
   * Logout mutation
   */
  const logoutMutation = useMutation({
    mutationFn: async () => {
      try {
        const refreshToken = getRefreshToken() ?? undefined;
        await authApi.logout(refreshToken);
      } catch {
        // Ignore logout API errors - we'll clear local state anyway
      }
    },
    onSettled: () => {
      // Clear tokens from storage
      clearTokens();

      // Clear auth store
      storeLogout();

      // Clear all cached queries
      queryClient.clear();

      // Show notification
      showInfo('You have been logged out');

      // Redirect to login
      navigate('/login', { replace: true });
    },
  });

  /**
   * Login function
   */
  const login = useCallback(
    async (credentials: LoginFormData) => {
      return loginMutation.mutateAsync(credentials);
    },
    [loginMutation]
  );

  /**
   * Register function
   */
  const register = useCallback(
    async (data: RegisterFormData) => {
      return registerMutation.mutateAsync(data);
    },
    [registerMutation]
  );

  /**
   * Logout function
   */
  const logout = useCallback(async () => {
    return logoutMutation.mutateAsync();
  }, [logoutMutation]);

  return {
    // State
    user,
    isAuthenticated,
    isLoading,
    isInitialized,
    currentWorkspaceId,

    // Login
    login,
    isLoginLoading: loginMutation.isPending,
    loginError: loginMutation.error ? parseApiError(loginMutation.error) : null,
    resetLoginError: loginMutation.reset,
    completeMcpGatewayLoginHandoff,

    // Register
    register,
    isRegisterLoading: registerMutation.isPending,
    registerError: registerMutation.error ? parseApiError(registerMutation.error) : null,
    resetRegisterError: registerMutation.reset,

    // Logout
    logout,
    isLogoutLoading: logoutMutation.isPending,
  };
};

/**
 * useAuthState hook
 * Lightweight hook for components that only need auth state (no actions)
 */
export const useAuthState = () => {
  const user = useCurrentUser();
  const isAuthenticated = useIsAuthenticated();
  const isLoading = useAuthLoading();
  const isInitialized = useAuthInitialized();

  return {
    user,
    isAuthenticated,
    isLoading,
    isInitialized,
  };
};

// ---------------------------------------------------------------------------
// Standalone mutation / query hooks (moved from api/hooks/useAuth.ts)
// ---------------------------------------------------------------------------

/**
 * Hook to fetch fresh user profile via React Query.
 * Use this on pages that need loading/error states for the user fetch
 * (e.g., Profile page). For synchronous access, use useCurrentUser from
 * the auth store instead.
 */
export const useProfileQuery = () => {
  const isAuthenticated = useIsAuthenticated();

  return useQuery({
    queryKey: CURRENT_USER_QUERY_KEY,
    queryFn: authApi.getCurrentUser,
    enabled: isAuthenticated,
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: false,
  });
};

/**
 * Hook to request password reset
 */
export const useForgotPassword = () => {
  const { success, error } = useSnackbar();

  return useMutation({
    mutationFn: (data: ForgotPasswordRequest) => authApi.forgotPassword(data),
    onSuccess: () => {
      success('Password reset email sent', {
        description: 'Please check your email for reset instructions',
        duration: 10000,
      });
    },
    onError: () => {
      error('Failed to send reset email', {
        description: 'Please try again or contact support',
      });
    },
  });
};

/**
 * Hook to reset password
 */
export const useResetPassword = () => {
  const { success, error } = useSnackbar();

  return useMutation({
    mutationFn: (data: ResetPasswordRequest) => authApi.resetPassword(data),
    onSuccess: () => {
      success('Password reset successful', {
        description: 'You can now login with your new password',
      });
    },
    onError: () => {
      error('Password reset failed', {
        description: 'The reset link may be invalid or expired',
      });
    },
  });
};

/**
 * Hook to change password
 */
export const useChangePassword = () => {
  const { success, error } = useSnackbar();

  return useMutation({
    mutationFn: (data: ChangePasswordRequest) => authApi.changePassword(data),
    onSuccess: () => {
      success('Password changed successfully');
    },
    onError: () => {
      error('Failed to change password', {
        description: 'Please check your current password',
      });
    },
  });
};

/**
 * Hook to update profile
 */
export const useUpdateProfile = () => {
  const setUser = useAuthStore((state) => state.setUser);
  const { success, error } = useSnackbar();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateProfileRequest) => authApi.updateProfile(data),
    onSuccess: (user) => {
      // updateProfile returns User directly (not wrapped in {user: ...})
      setUser(user);
      queryClient.setQueryData(CURRENT_USER_QUERY_KEY, user);
      success('Profile updated successfully');
    },
    onError: () => {
      error('Failed to update profile');
    },
  });
};

/**
 * Hook to get active sessions
 */
export const useSessions = () => {
  const isAuthenticated = useIsAuthenticated();

  return useQuery({
    queryKey: SESSIONS_QUERY_KEY,
    queryFn: authApi.getSessions,
    enabled: isAuthenticated,
    staleTime: 60 * 1000, // 1 minute
  });
};

/**
 * Hook to revoke all sessions
 */
export const useRevokeAllSessions = () => {
  const logoutStore = useAuthStore((state) => state.logout);
  const { success, error } = useSnackbar();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: authApi.revokeAllSessions,
    onSuccess: () => {
      success('All sessions revoked');
      clearTokens();
      logoutStore();
      queryClient.clear();
      navigate('/login', { replace: true });
    },
    onError: () => {
      error('Failed to revoke sessions');
    },
  });
};
